import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Select, Slider, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography, CircularProgress,
} from '@mui/material';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.js';
import { episodesAPI, MegaphoneCuepoint } from '../../services/api';

const AD_SOURCES = ['auto', 'promo', 'direct_only'] as const;
type AdSource = typeof AD_SOURCES[number];
type CueType = 'preroll' | 'midroll' | 'postroll';

// One editable cuepoint (existing OR new — the whole set is now editable).
interface Cue {
  id: string;          // local React/region key
  type: CueType;
  at: number;          // seconds
  count: number;
  source: AdSource;
}

interface Props {
  open: boolean;
  onClose: () => void;
  site: string;
  megaphoneEpisodeId: string;
  episodeTitle?: string;
  onSaved?: (cuepoints: MegaphoneCuepoint[]) => void;
}

const MARKER_COLOR: Record<string, string> = {
  preroll: 'rgba(63,81,181,0.35)',
  midroll: 'rgba(150,76,94,0.45)',
  postroll: 'rgba(237,108,2,0.35)',
};

function fmt(sec: number): string {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

// "1:23" / "1:02:03" / "83" → seconds. Empty/garbage → null (keep old value).
function parseTs(v: string): number | null {
  const t = (v || '').trim();
  if (!t) return null;
  if (/^\d+(\.\d+)?$/.test(t)) return parseFloat(t);
  const parts = t.split(':').map((p) => parseInt(p, 10));
  if (parts.some((n) => isNaN(n))) return null;
  return parts.reduce((acc, n) => acc * 60 + n, 0);
}

const srcOf = (c: MegaphoneCuepoint): AdSource => {
  const s = (c.ad_sources || [])[0];
  return (AD_SOURCES as readonly string[]).includes(s) ? (s as AdSource) : 'auto';
};

let _cid = 0;
const newId = () => `cue_${++_cid}`;

const MidrollPlacer: React.FC<Props> = ({
  open, onClose, site, megaphoneEpisodeId, episodeTitle, onSaved,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const regionsRef = useRef<any>(null);
  const objectUrlRef = useRef<string | null>(null);
  const regionByCue = useRef<Map<string, any>>(new Map());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [zoom, setZoom] = useState(40);
  const [audioSource, setAudioSource] = useState<string>('');

  const [cues, setCues] = useState<Cue[]>([]);
  const [orig, setOrig] = useState<string>('[]');   // snapshot for dirty-check
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  const cuesRef = useRef<Cue[]>([]);
  cuesRef.current = cues;
  const dirty = JSON.stringify(cues.map((c) => ({ ...c, id: undefined }))) !== orig;

  // ── draw a region per cue (midrolls draggable; pre/post fixed) ──
  const draw = useCallback((list: Cue[]) => {
    const regions = regionsRef.current;
    if (!regions) return;
    try {
      regions.clearRegions();
      regionByCue.current.clear();
      list.forEach((c) => {
        const r = regions.addRegion({
          id: c.id,
          start: c.at,
          content: `${c.type} ×${c.count}`,
          color: MARKER_COLOR[c.type] || 'rgba(0,0,0,0.3)',
          drag: c.type === 'midroll',
          resize: false,
        });
        regionByCue.current.set(c.id, r);
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('marker draw failed', e);
    }
  }, []);

  // ── init wavesurfer + load cuepoints when dialog opens ──
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true); setError(null); setReady(false); setSaved(null);

    (async () => {
      try {
        const [cueResp, audio] = await Promise.all([
          episodesAPI.getMegaphoneCuepoints(site, megaphoneEpisodeId),
          episodesAPI.megaphoneAudioObjectUrl(site, megaphoneEpisodeId),
        ]);
        const objUrl = audio.url;
        if (cancelled) { URL.revokeObjectURL(objUrl); return; }
        objectUrlRef.current = objUrl;
        setAudioSource(audio.source);

        const loaded: Cue[] = (cueResp.cuepoints || [])
          .map((c) => ({
            id: newId(), type: (c.type as CueType) || 'midroll',
            at: c.start_time || 0, count: c.ad_count || 1, source: srcOf(c),
          }))
          .sort((a, b) => a.at - b.at);
        setCues(loaded);
        setOrig(JSON.stringify(loaded.map((c) => ({ ...c, id: undefined }))));

        const regions = RegionsPlugin.create();
        regionsRef.current = regions;
        const ws = WaveSurfer.create({
          container: containerRef.current as HTMLElement,
          height: 140, waveColor: '#b9a3ab', progressColor: '#964c5e',
          cursorColor: '#222', minPxPerSec: zoom, url: objUrl, plugins: [regions],
        });
        wsRef.current = ws;
        ws.on('ready', () => {
          if (cancelled) return;
          setDuration(ws.getDuration());
          setReady(true); setLoading(false);
          draw(loaded);
        });
        ws.on('timeupdate', (t: number) => setCurrent(t));
        ws.on('play', () => setPlaying(true));
        ws.on('pause', () => setPlaying(false));
        ws.on('finish', () => setPlaying(false));
        // drag a midroll → update its timestamp (no full redraw needed)
        regions.on('region-updated', (region: any) => {
          const cue = cuesRef.current.find((c) => c.id === region.id);
          if (cue && cue.type === 'midroll') {
            setCues((prev) => prev.map((c) =>
              c.id === region.id ? { ...c, at: region.start } : c));
          }
        });
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e?.message || 'failed to load audio');
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      try { wsRef.current?.destroy(); } catch { /* noop */ }
      wsRef.current = null; regionsRef.current = null; regionByCue.current.clear();
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, site, megaphoneEpisodeId]);

  useEffect(() => {
    if (ready && wsRef.current) {
      try { wsRef.current.zoom(zoom); } catch { /* noop */ }
    }
  }, [zoom, ready]);

  const addMidroll = () => {
    const at = wsRef.current ? wsRef.current.getCurrentTime() : current;
    if (duration && (at <= 0 || at >= duration)) return;
    const cue: Cue = { id: newId(), type: 'midroll', at, count: 4, source: 'auto' };
    setCues((prev) => [...prev, cue].sort((a, b) => a.at - b.at));
    const regions = regionsRef.current;
    if (regions) {
      const r = regions.addRegion({
        id: cue.id, start: at, content: `midroll ×4`,
        color: MARKER_COLOR.midroll, drag: true, resize: false,
      });
      regionByCue.current.set(cue.id, r);
    }
  };

  const updateCue = (id: string, patch: Partial<Cue>) => {
    setCues((prev) => {
      const next = prev.map((c) => (c.id === id ? { ...c, ...patch } : c));
      // keep the region label / position in sync for count or time edits
      const r = regionByCue.current.get(id);
      const c = next.find((x) => x.id === id);
      if (r && c) {
        try {
          if (patch.at != null) r.setOptions({ start: c.at });
          if (patch.count != null || patch.type != null) {
            r.setOptions({ content: `${c.type} ×${c.count}` });
          }
        } catch { /* noop */ }
      }
      return next;
    });
  };

  const removeCue = (id: string) => {
    const r = regionByCue.current.get(id);
    try { r?.remove(); } catch { /* noop */ }
    regionByCue.current.delete(id);
    setCues((prev) => prev.filter((c) => c.id !== id));
  };

  const save = async () => {
    setSaving(true); setError(null); setSaved(null);
    try {
      const res = await episodesAPI.setCuepoints({
        site, megaphone_episode_id: megaphoneEpisodeId,
        cuepoints: [...cues]
          .sort((a, b) => a.at - b.at)
          .map((c) => ({ type: c.type, at: String(c.at), count: c.count, source: c.source })),
      });
      const reloaded: Cue[] = (res.cuepoints || []).map((c) => ({
        id: newId(), type: (c.type as CueType) || 'midroll',
        at: c.start_time || 0, count: c.ad_count || 1, source: srcOf(c),
      })).sort((a, b) => a.at - b.at);
      setCues(reloaded);
      setOrig(JSON.stringify(reloaded.map((c) => ({ ...c, id: undefined }))));
      draw(reloaded);
      setSaved(`Saved — episode now has ${res.after_count} ad markers `
        + `(${reloaded.filter((c) => c.type === 'midroll').length} midroll).`);
      onSaved?.(res.cuepoints);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'save failed');
    } finally {
      setSaving(false);
    }
  };

  const sorted = [...cues].sort((a, b) => a.at - b.at);

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle>
        Edit ad markers{episodeTitle ? ` — ${episodeTitle}` : ''}
        <Typography variant="body2" color="text.secondary">
          {site} · edit any placement (move/retime, change positions or source,
          delete) — what you save IS the episode's full cuepoint set.
        </Typography>
        {audioSource === 'upload' && (
          <Typography variant="caption" sx={{ color: 'success.main', display: 'block' }}>
            ✓ waveform from the uploaded master (sting-free) — offsets are accurate
          </Typography>
        )}
        {audioSource === 'megaphone' && (
          <Typography variant="caption" sx={{ color: 'warning.main', display: 'block' }}>
            ⚠ waveform from Megaphone audio (includes the network sting) — offsets
            may be off by the sting length
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {saved && <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>}

        <Box sx={{ position: 'relative', minHeight: 150 }}>
          {loading && (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }} spacing={1}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">loading waveform…</Typography>
            </Stack>
          )}
          <div ref={containerRef} style={{ width: '100%' }} />
        </Box>

        {ready && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={() => wsRef.current?.playPause()}>
              {playing ? '⏸ Pause' : '▶ Play'}
            </Button>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {fmt(current)} / {fmt(duration)}
            </Typography>
            <Button variant="contained" onClick={addMidroll}>＋ Add midroll at playhead</Button>
            <Box sx={{ width: 220, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption">zoom</Typography>
              <Slider size="small" min={10} max={400} value={zoom}
                onChange={(_, v) => setZoom(v as number)} />
            </Box>
          </Stack>
        )}

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Ad locations</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Timestamp</TableCell>
              <TableCell>Ad positions</TableCell>
              <TableCell>Ad source</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  <Chip size="small" label={c.type}
                    color={c.type === 'midroll' ? 'secondary' : 'default'} />
                </TableCell>
                <TableCell>
                  {c.type === 'midroll' ? (
                    <TextField
                      size="small" variant="standard" defaultValue={fmt(c.at)}
                      sx={{ width: 90 }}
                      onBlur={(e) => {
                        const s = parseTs(e.target.value);
                        if (s != null) updateCue(c.id, { at: s });
                        else e.target.value = fmt(c.at);
                      }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {fmt(c.at)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <TextField
                    size="small" type="number" variant="standard" value={c.count}
                    onChange={(e) => updateCue(c.id, {
                      count: Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1)),
                    })}
                    sx={{ width: 70 }} inputProps={{ min: 1, max: 10 }}
                  />
                </TableCell>
                <TableCell>
                  <Select size="small" variant="standard" value={c.source}
                    onChange={(e) => updateCue(c.id, { source: e.target.value as AdSource })}>
                    {AD_SOURCES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => removeCue(c.id)} aria-label="delete">🗑</IconButton>
                </TableCell>
              </TableRow>
            ))}
            {sorted.length === 0 && (
              <TableRow><TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary">
                  No ad markers — add a midroll above, then save.
                </Typography>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        {dirty && (
          <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
            Unsaved changes — “Save changes” writes this exact set to Megaphone.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={save} disabled={saving || !dirty}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MidrollPlacer;
